import React, { Dispatch, SetStateAction } from 'react';

import { Box, Slider, Typography } from '@mui/material';
import { Button, LoadingButton } from '@apigrowfinance/pbolt-button';
import CurrencyInput from '@apigrowfinance/pbolt-currency-input';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import NumericTextField from '@apigrowfinance/pbolt-numeric-textfield';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import {
    IPatchUpdateAccountLimitsDetails,
    LimitsDetails,
} from '@clients/accountsClient';
import { useAccount } from '@contexts/AccountProvider';
import usePatchLimits from '@hooks/queries/usePatchLimits';
import getEnumTranslation from '@utils/getEnumTranslation';

export type EditLimitsFormProps = {
    limits: LimitsDetails;
    setOpen: Dispatch<SetStateAction<boolean>>;
    refetch: () => void;
};

const EditLimitsForm = ({ limits, setOpen, refetch }: EditLimitsFormProps) => {
    const {
        handleSubmit,
        control,
        formState: { isDirty },
    } = useForm({
        defaultValues: {
            limit: limits?.total_credit_limit,
            total_overdraft_limit: limits?.total_overdraft_limit,
            percentage_over_limit: limits?.percentage_over_limit,
        },
    });

    const { account, program } = useAccount();

    const currency = program?.currency_numeric_code;

    const { mutate, isLoading } = usePatchLimits(account?.account_id, () => {
        refetch();
        setOpen(false);
    });

    const onSubmit = (data: IPatchUpdateAccountLimitsDetails) => {
        mutate(data);
    };

    return (
        <form
            data-testid="transactions-filter-form"
            style={{ width: '100%' }}
            onSubmit={handleSubmit(onSubmit)}>
            <PDialogContent>
                <Box>
                    <Typography variant="overline" sx={{ fontWeight: 600 }}>
                        <FormattedMessage
                            id="EditLimitsForm.totalCreditLimit"
                            defaultMessage="Total credit limit"
                        />
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        mb: 4,
                    }}>
                    <Controller
                        name="limit"
                        control={control}
                        render={({ field }) => (
                            <Slider
                                min={1}
                                max={limits?.max_credit_limit}
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="limit"
                        control={control}
                        render={({ field }) => (
                            <CurrencyInput
                                {...field}
                                currency={currency}
                                sx={{ mb: 0, ml: 2 }}
                            />
                        )}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Controller
                        name="total_overdraft_limit"
                        control={control}
                        render={({ field }) => (
                            <CurrencyInput
                                {...field}
                                currency="GBP"
                                label={
                                    <FormattedMessage
                                        id="EditLimitsForm.totalOverdraftLimit"
                                        defaultMessage="Overdraft"
                                    />
                                }
                            />
                        )}
                    />
                    <Controller
                        name="percentage_over_limit"
                        control={control}
                        render={({ field }) => (
                            <NumericTextField
                                {...field}
                                numericFormatOptions={{ suffix: '%' }}
                                label={
                                    <FormattedMessage
                                        id="EditLimitsForm.percentageOverLimit"
                                        defaultMessage="Percentage over"
                                    />
                                }
                            />
                        )}
                    />
                </Box>
            </PDialogContent>
            <PDialogAction>
                <Button onClick={() => setOpen(false)}>
                    {getEnumTranslation('cancel')}
                </Button>
                <LoadingButton
                    disabled={!isDirty || isLoading}
                    loading={isLoading}>
                    {getEnumTranslation('save')}
                </LoadingButton>
            </PDialogAction>
        </form>
    );
};

export default EditLimitsForm;
